/**
 * What a slideshow visitor's device looks like to the viewer: input, screen
 * class, shape and orientation. Slideshow, SpreadViewer and ImageViewer all
 * read this one answer, and the Appearance editor can override it so the
 * preview frame renders what a phone or tablet would.
 */

export const SLIDESHOW_BREAKPOINT = 768;
export const SLIDESHOW_WIDE_RATIO = 1.2;
/** A screen whose short side is below this is a phone, not a tablet. */
export const PHONE_SCREEN_MAX = 500;

export const SLIDESHOW_VIEWS = Object.freeze(['auto', 'intro', 'text', 'image', 'last']);
/** Appearance frame URL param: the view to load with, before the editor's state arrives. */
export const SLIDESHOW_VIEW_QUERY = 'slideshowView';

/** @returns {'big-wide' | 'big-tall' | 'small'} */
export function screenShapeFor(width, height, {
    breakpoint = SLIDESHOW_BREAKPOINT,
    wideRatio = SLIDESHOW_WIDE_RATIO,
} = {}) {
    const w = Number(width) || 0;
    const h = Number(height) || 0;
    if (w < breakpoint) return 'small';
    return w > h * wideRatio ? 'big-wide' : 'big-tall';
}

/** Editor override: `touch` true/false forces the input, null leaves the device's own. */
export function normalizeExperienceOverride(value) {
    if (!value || typeof value !== 'object') return null;
    const touch = typeof value.touch === 'boolean' ? value.touch : null;
    const view = SLIDESHOW_VIEWS.includes(value.view) ? value.view : 'auto';
    const imageId = typeof value.imageId === 'string' && value.imageId.trim()
        ? value.imageId.trim().slice(0, 120)
        : null;
    if (touch === null && view === 'auto' && !imageId) return null;
    return { touch, view, imageId };
}

/**
 * @param {object} input
 * @param {number} input.width - viewport width (innerWidth)
 * @param {number} input.height - viewport height (innerHeight)
 * @param {number} input.screenMin - short side of the physical screen; 0 before measuring
 * @param {boolean} input.touch - touch-primary input
 * @param {object|null} [input.override] - editor override, see normalizeExperienceOverride
 */
export function resolveExperience({ width = 0, height = 0, screenMin = 0, touch = false, override = null } = {}) {
    const forced = normalizeExperienceOverride(override);
    const w = Number(width) || 0;
    const h = Number(height) || 0;
    const forcedTouch = typeof forced?.touch === 'boolean';
    // The editor's desktop preset is a desktop screen whatever shape the frame
    // has beside the inspector, so the spread (and its editing) always shows.
    const forcedDesktop = forced?.touch === false;
    // A forced device is the frame itself: its size is the screen size.
    const shortSide = forcedTouch ? Math.min(w, h) : Number(screenMin) || 0;
    const frameShape = screenShapeFor(w, h);
    const shape = forcedDesktop && frameShape === 'small' ? 'big-tall' : frameShape;
    const landscape = w > h;
    const phone = !forcedDesktop && shortSide > 0 && shortSide < PHONE_SCREEN_MAX;
    return {
        touch: forcedTouch ? forced.touch : Boolean(touch),
        phone,
        shape,
        landscape,
        portrait: h >= w && h > 0,
        phoneLayout: !forcedDesktop && (shape === 'small' || (shape === 'big-tall' && !landscape)),
        view: forced?.view || 'auto',
        imageId: forced?.imageId || null,
        forced: Boolean(forced),
    };
}
